import { IrisDMSFileI } from '@iris/common/modules/dms/models/IrisDMSFile';

export enum DmsFileNamePart {
  Name = 'name',
  BaseName = 'baseName',
  FullBaseName = 'fullBaseName',
  Version = 'version',
  Extension = 'extension',
  ExtensionPart = 'extensionPart',
}

export function dataURItoFile(dataURI, fileName, mimeType = 'image/png'): File {
  if (!dataURI) {
    return null;
  }
  // convert base64/URLEncoded data component to raw binary data held in a string
  let byteString;
  if (dataURI.split(',')[0].indexOf('base64') >= 0) {
    byteString = atob(dataURI.split(',')[1]);
  } else {
    // decodes a base-64 encoded string
    byteString = decodeURI(dataURI.split(',')[1]);
  }

  // write the bytes of the string to a typed array
  const ia = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new File([ia], fileName, { type: mimeType });
}

export function base64ToBlob(base64: string, mimeType = 'image/png'): Blob {
  // function for pure base64 string without metadata
  const binaryString = atob(base64);
  const byteArray = new Uint8Array(binaryString.length);

  for (let i = 0; i < binaryString.length; i++) {
    byteArray[i] = binaryString.charCodeAt(i);
  }

  return new Blob([byteArray], { type: mimeType });
}

export function blobToBase64(blob: Blob, forcedMimeType?: string): Promise<string> {
  return new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      let result = reader.result as string;
      if (forcedMimeType) {
        const base64Data = result.split(',')[1];
        result = `data:${forcedMimeType};base64,${base64Data}`;
      }
      resolve(result);
    };
    reader.readAsDataURL(blob);
  });
}

export const getFileExtensionByName = (name: string): string => {
  return getFileNamePart(name, DmsFileNamePart.Extension);
};

export const getNewFileName = (existingFilesNames: string[], file: IrisDMSFileI, newFileName: string): string => {
  const fileExtension = getFileNamePart(file.name, DmsFileNamePart.ExtensionPart);
  const baseName = getFileNamePart(newFileName, DmsFileNamePart.BaseName);
  const fileNameVersion = calcFileNameVersion(existingFilesNames, `${baseName}${fileExtension}`);
  return `${baseName} (${fileNameVersion})${fileExtension}`;
};

export const getFileNamePart = (fileName: string, filePart: DmsFileNamePart): string => {
  const extension = fileName?.includes('.') ? fileName.split('.').pop() : '';
  const extensionPart = extension ? `.${extension}` : '';
  const name = fileName ? fileName.substring(0, fileName.length - extensionPart.length) : '';
  const [baseName, version] = name.split(/ \(([0-9]+)\)/);

  switch (filePart) {
    case DmsFileNamePart.Name:
      return name;
    case DmsFileNamePart.BaseName:
      return baseName;
    case DmsFileNamePart.FullBaseName:
      return `${baseName}${extensionPart}`;
    case DmsFileNamePart.Version:
      return version;
    case DmsFileNamePart.ExtensionPart:
      return extensionPart;
    case DmsFileNamePart.Extension:
      return extension.toUpperCase();
  }
};

const calcFileNameVersion = (filesNames: string[], fileName: string): number => {
  const isFileNameVersionAlreadyExists = filesNames
    .map(name => getFileNamePart(name, DmsFileNamePart.FullBaseName))
    .filter(name => name === fileName).length > 1;

  if (!isFileNameVersionAlreadyExists) {
    return 2;
  }

  const versionsList = filesNames
    .filter(name => getFileNamePart(name, DmsFileNamePart.FullBaseName) === fileName)
    .filter(name => !!getFileNamePart(name, DmsFileNamePart.Version))
    .map(name => +(/(\d+)/.exec(getFileNamePart(name, DmsFileNamePart.Version)))[0]);

  const maxVersion = Math.max(...versionsList);
  const missingVersions = Array
    .from(Array(maxVersion), (element, i) => !versionsList.includes(i) ? i : null)
    .filter(version => !!version && version > 1);

  return missingVersions.length ? missingVersions[0] : maxVersion + 1;
};

export function downloadFileFromDataUrl(dataUrlString: string, fileName: string): void {
  if (!isDataUrl(dataUrlString)) {
    throw new Error('Provided string is not a valid data URL');
  }
  const link = document.createElement('a');
  link.href = dataUrlString;
  link.download = fileName;
  link.click();
  
  link.remove();
}

export function isDataUrl(link: string): boolean {
  if (!link || typeof link !== 'string') return false;
  try {
    return new URL(link).protocol === 'data:';
  } catch {
    return false;
  }
}
